import { useCallback, useEffect, useMemo } from 'react'
import { rem } from 'polished'

import CardCarousel from 'LuxKit/Carousel/CardCarousel'
import TourV2PackageOptionDetailsTile from 'checkout/Components/Tours/TourV2PackageOptionDetailsTile'
import { useAppSelector } from 'hooks/reduxHooks'
import { getIsLuxPlusToursEnabled } from 'checkout/selectors/tourV2Selectors'
import generateDepartureRoomPriceBreakdown from 'lib/tours/generateDepartureRoomPriceBreakdown'
import { getRoomTypeCapacity } from 'lib/tours/tourUtils'
import { sortBy } from 'lib/array/arrayUtils'

const EMPTY_GEO_CONTACT = {} as App.GeoContact

function getCheapestPurchasableOptionForPackageType(params: {
  offer: App.Tours.TourV2Offer
  variationId: App.Tours.TourV2OfferVariation['id']
  packageType?: string
  isLuxPlusToursAvailable: boolean
}): App.Tours.TourV2OfferPurchasableOption | undefined {
  const {
    offer,
    variationId,
    packageType,
    isLuxPlusToursAvailable,
  } = params

  const optionsForVariationAndPackage = offer.purchasableOptions.filter((option) => {
    if (option.fkVariationId !== variationId) return false
    if (!packageType) return true
    return option.packageType === packageType
  })

  if (optionsForVariationAndPackage.length === 0) return undefined

  const isDepartureAvailable = (option: App.Tours.TourV2OfferPurchasableOption) => {
    const departure = offer.departures[option.fkDepartureId]
    return departure?.status === 'available'
  }

  const available = optionsForVariationAndPackage.filter(isDepartureAvailable)
  // If a package type has no available departures, do not show it.
  if (available.length === 0) return undefined

  return available.reduce<App.Tours.TourV2OfferPurchasableOption | undefined>((cheapest, option) => {
    const optionPrice = isLuxPlusToursAvailable && option.memberPrice ? option.memberPrice : option.price
    const cheapestPrice = cheapest ? (isLuxPlusToursAvailable && cheapest.memberPrice ? cheapest.memberPrice : cheapest.price) : undefined
    if (!cheapest || cheapestPrice === undefined || optionPrice < cheapestPrice) return option
    return cheapest
  }, undefined)
}

interface Props {
  offer: App.Tours.TourV2Offer
  variation: App.Tours.TourV2OfferVariation
  selectedPackageType?: string
  onSelectedPackageTypeChange?: (packageType: string | undefined) => void
}

interface PackageModel {
  key: string
  packageOption: App.Tours.PackageUpgrade
  purchasableOption: App.Tours.TourV2OfferPurchasableOption
  fromPrice: number
}

function TourV2VariationPackages(props: Props) {
  const { offer, variation, selectedPackageType, onSelectedPackageTypeChange } = props

  const isLuxPlusToursAvailable = useAppSelector((state) => getIsLuxPlusToursEnabled(state))

  const packageUpgrades = useMemo(() => variation.availablePackageUpgrades ?? [], [variation.availablePackageUpgrades])
  const packageModels = useMemo(() => {
    const models = packageUpgrades.reduce<Array<PackageModel>>((acc, packageOption) => {
      const purchasableOption = getCheapestPurchasableOptionForPackageType({
        offer,
        variationId: variation.id,
        packageType: packageOption.packageType,
        isLuxPlusToursAvailable,
      })

      if (!purchasableOption) return acc

      const fromPrice = isLuxPlusToursAvailable && purchasableOption.memberPrice
        ? purchasableOption.memberPrice
        : purchasableOption.price

      acc.push({
        key: packageOption.packageType ?? purchasableOption.packageType ?? purchasableOption.fkDepartureId,
        packageOption,
        purchasableOption,
        fromPrice,
      })

      return acc
    }, [])

    return sortBy(models, model => model.fromPrice, 'asc')
  }, [offer, packageUpgrades, variation.id, isLuxPlusToursAvailable])

  useEffect(() => {
    const availablePackageTypes = new Set(packageModels.map(model => model.purchasableOption.packageType))
    if (selectedPackageType && availablePackageTypes.has(selectedPackageType)) return

    const defaultPackageType = packageModels[0]?.purchasableOption.packageType
    if (defaultPackageType) onSelectedPackageTypeChange?.(defaultPackageType)
  }, [packageModels, selectedPackageType, onSelectedPackageTypeChange])

  const onPackageOptionSelect = useCallback((departure: App.Tours.TourV2OfferPurchasableOption) => {
    if (departure.packageType) onSelectedPackageTypeChange?.(departure.packageType)
  }, [onSelectedPackageTypeChange])

  const renderPackageTile = useCallback((model: PackageModel, packageOptionIndex: number) => {
    const { packageOption, purchasableOption, key } = model
    const departure = offer.departures[purchasableOption.fkDepartureId]
    const isInventoryAvailable = departure?.status === 'available'
    const adults = getRoomTypeCapacity(purchasableOption.roomType) || 2
    const occupancy: App.Occupants = { adults }
    const priceBreakdown = generateDepartureRoomPriceBreakdown(occupancy, purchasableOption, offer)
    const tourItems = [{ occupancy }] as Array<App.Checkout.TourV2Item>
    const purchasableOptionWithPriceBreakdown: Array<[App.Tours.TourV2OfferPurchasableOption, App.Tours.V2OfferDeparturePriceBreakdown]> = [[purchasableOption, priceBreakdown]]

    return <TourV2PackageOptionDetailsTile
      key={key}
      tourOffer={offer}
      tourItems={tourItems}
      packageOption={packageOption}
      packageOptionIndex={packageOptionIndex}
      purchasableOption={purchasableOption}
      purchasableOptionWithPriceBreakdown={purchasableOptionWithPriceBreakdown}
      isSelected={purchasableOption.packageType === selectedPackageType}
      onPackageOptionSelect={onPackageOptionSelect}
      contact={EMPTY_GEO_CONTACT}
      isInventoryAvailable={isInventoryAvailable}
      trackAnalytics={false}
    />
  }, [offer, onPackageOptionSelect, selectedPackageType])

  const packageTiles = useMemo(() => {
    return packageModels
      .slice(0, 8)
      .map(renderPackageTile)
  }, [packageModels, renderPackageTile])

  if (packageTiles.length === 0) return null

  return <CardCarousel
    width={`min(${rem(380)}, 85%)`}
    tabletWidth={`min(${rem(420)}, 75%)`}
    gap={12}
  >
    {packageTiles}
  </CardCarousel>
}

export default TourV2VariationPackages
